import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Modal, Button, Table } from 'react-bootstrap';
import { GetServiceCaller } from '../../../../services/serviceCaller';
import { API_URL } from '../../../../constants';
import CRDynamic_PR_table from '../../../../components/CRDynamic_PR_table';
import { useDispatch } from 'react-redux';
import { updateNavbarHeading } from '../../../../constants/layout/store/filterSlice';
import moment from 'moment';
import { FaSpinner } from 'react-icons/fa';

const CENTER_ALIGNED_KEYS = new Set([
    'R1 Stage', 'R2 Stage', 'Client/R3 Stage', 'HR Round', 'Offer Stage',
    'Addressed', 'Client Select', 'Internal', 'SubCon', 'Fulltime', 'Total'
]);

const REQ_ID_KEYS = new Set(['Req ID', 'Requisition ID', 'RequisitionID', 'RequisitionSubID']);

const INITIAL_COLUMNS = [
    'Req ID', 'RMG JC', 'Customer', 'Line of Business', 'Track', 'Job Family',
    'Primary Skill', 'Secondary Skill', 'Client Intv', 'Local/Global',
    'Location', 'Band', 'Experience', 'Personal Area', 'Personal Sub Area',
    'Requisition Source', 'Employee Group', 'Billing Type', 'RMG to TA', 'RMG POC',
    'TA POC 1', 'TA POC 2', 'Date of Opening', 'Date of Closing', 'Overall Ageing',
    'TA Ageing', 'Job Status', 'RMG Status', 'Requisition Status', 'Ageing Slab', 'RMG Remarks',
    'TA Remarks', 'R1 Stage', 'R2 Stage', 'Client/R3 Stage', 'HR Round',
    'Offer Stage', 'Addressed', 'Client Select', 'Internal', 'SubCon', 'Fulltime', 'Total'
].reduce((acc, key) => {
    acc[key] = { 
        text: key, 
        align: CENTER_ALIGNED_KEYS.has(key) ? 'center' : 'left', 
        visible: true,
        type: REQ_ID_KEYS.has(key) ? 'Modal' : undefined,
        width: REQ_ID_KEYS.has(key) ? '125px' : undefined
    };
    return acc;
}, {});

// Auto-derive: every column is visible by default
const INITIAL_DEFAULT_COLUMNS = Object.fromEntries(
    Object.keys(INITIAL_COLUMNS).map(k => [k, true])
);

const RequisitionTrackerReport = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(updateNavbarHeading("Requisition Tracker Report"));
    }, [dispatch]);

    const [reportData, setReportData]   = useState([]);
    const [apiStatus,  setApiStatus]    = useState(undefined);
    const [headerOffset, setHeaderOffset] = useState(0);
    const [bottomOffset, setBottomOffset] = useState(0);
    const wrapperRef = useRef(null);
    const [searchTerm, setSearchTerm]   = useState("");

    // Candidate Leads Modal State
    const [showCandidateModal, setShowCandidateModal] = useState(false);
    const [candidateModalData, setCandidateModalData] = useState({ taCount: 0, rmgCount: 0, candidates: [] });
    const [modalLoading, setModalLoading]             = useState(false);
    const [modalReqCode, setModalReqCode]             = useState('');

    const handleModalColumn = async (columnName, item) => {
        if (REQ_ID_KEYS.has(columnName)) {
            const targetId = item?.ID || item[columnName];
            if (!targetId) return;
            const reqCode = item[columnName] || targetId;
            setModalReqCode(reqCode);
            setShowCandidateModal(true);
            setModalLoading(true);
            try {
                const response = await GetServiceCaller(`${API_URL}requirement/${targetId}/leads`);
                if (response?.data?.status === 'success' || response?.status === 200) {
                    const payload = response?.data;
                    setCandidateModalData({
                        taCount: payload?.taCount ?? 0,
                        rmgCount: payload?.rmgCount ?? 0,
                        candidates: payload?.data || []
                    });
                } else {
                    setCandidateModalData({ taCount: 0, rmgCount: 0, candidates: [] });
                }
            } catch (error) {
                console.error(error);
                setCandidateModalData({ taCount: 0, rmgCount: 0, candidates: [] });
            } finally {
                setModalLoading(false);
            }
        }
    };

    
    useEffect(() => {
        const measure = () => {
            if (!wrapperRef.current) return;
            const thead         = wrapperRef.current.querySelector('thead');
            const tableWrapper  = wrapperRef.current.querySelector('.table-responsive');
            if (thead && tableWrapper) {
                const wrapperRect      = wrapperRef.current.getBoundingClientRect();
                const theadBottom      = thead.getBoundingClientRect().bottom;
                const tableWrapperBottom = tableWrapper.getBoundingClientRect().bottom;
                setHeaderOffset(Math.round(theadBottom      - wrapperRect.top));
                setBottomOffset(Math.round(wrapperRect.bottom - tableWrapperBottom));
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [apiStatus]);

    // Filter dropdown options
    const [statuses,     setStatuses]     = useState([]);
    const [requisitions, setRequisitions] = useState([]);
    const [recruiters,   setRecruiters]   = useState([]);
    const [skills,       setSkills]       = useState([]);

    const [filterFormData, setFilterData] = useState([
        { label: "From Date",     value: null },
        { label: "To Date",       value: null },
        { label: "Status",        value: "" },
        { label: "Requisition ID",value: "" },
        { label: "Recruiter",     value: "" },
        { label: "Skills",        value: "" }
    ]);

     useEffect(() => {
        const COLOR_MAP = {
            '0-30 Days':  { bg: '#198754', color: 'white' },
            '31-60 Days': { bg: '#ffc107', color: 'black' },
            '61-90 Days': { bg: '#E3242B', color: 'white' },
            '91+ Days':   { bg: '#900603', color: 'white' },
            '91 Days +':  { bg: '#dc3545', color: 'white' },
            '31 Days +':  { bg: '#dc3545', color: 'white' },
        };

        const styleSlabs = () => {
            document.querySelectorAll('td').forEach(cell => {
                if (cell.querySelector('.ageing-slab-badge')) return;
                const text = (cell.innerText || cell.textContent || '').trim();
                const style = COLOR_MAP[text];
                if (style) {
                    cell.innerHTML = `<span class="ageing-slab-badge" style="background-color:${style.bg};color:${style.color};padding:4px 8px;border-radius:4px;font-weight:bold;font-size:11px;display:inline-block;">${text}</span>`;
                }
            });
        };

        styleSlabs();
        const observer = new MutationObserver(styleSlabs);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [reportData, searchTerm]);

    const fetchReport = async () => {
        setApiStatus(undefined);
        try {
            const getValue = (label) =>
                filterFormData.find(o => o.label === label)?.value ?? '';

            const fDate = getValue('From Date')
                ? moment(getValue('From Date'), ["DD-MMM-YYYY", "YYYY-MM-DD"]).format('YYYY-MM-DD')
                : '';
            const tDate = getValue('To Date')
                ? moment(getValue('To Date'), ["DD-MMM-YYYY", "YYYY-MM-DD"]).format('YYYY-MM-DD')
                : '';

            const response = await GetServiceCaller(
                `${API_URL}report/14` +
                `?P_FromDate=${fDate}&P_ToDate=${tDate}` +
                `&P_Status=${getValue('Status')}` +
                `&P_RequisitionID=${getValue('Requisition ID')}` +
                `&P_RecruiterID=${getValue('Recruiter')}` +
                `&P_SkillID=${getValue('Skills')}`
            );

            setApiStatus(response?.status);
            if (response?.status === 200) {
                const payload = response.data.data;
                setReportData(payload.reportData   || []);
                if (payload.statuses)     setStatuses(payload.statuses);
                if (payload.requisitions) setRequisitions(payload.requisitions);
                if (payload.recruiters)   setRecruiters(payload.recruiters);
                if (payload.skills)       setSkills(payload.skills);
            } else {
                setReportData([]);
            }
        } catch (error) {
            console.error(error);
            setApiStatus(500);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e, name) => {
        setFilterData(prev => {
            const idx = prev.findIndex(item => item.label === name);
            const val = (e && e.value !== undefined) ? e.value : e;
            return idx !== -1
                ? prev.map((item, i) => i === idx ? { ...item, value: val } : item)
                : [...prev, { label: name, value: val }];
        });
    };

    const handleReset = () => setFilterData([
        { label: "From Date",     value: null },
        { label: "To Date",       value: null },
        { label: "Status",        value: "" },
        { label: "Requisition ID",value: "" },
        { label: "Recruiter",     value: "" },
        { label: "Skills",        value: "" }
    ]);

    // ── Columns: fall back to INITIAL_COLUMNS while data is loading ──────────
    const { tableColumns, defaultTableColumns } = useMemo(() => {
        if (!reportData?.length) {
            return { tableColumns: INITIAL_COLUMNS, defaultTableColumns: INITIAL_DEFAULT_COLUMNS };
        }
        const tCols = {};
        const dCols = {};
        Object.keys(reportData[0]).forEach(key => {
            if (key === 'ID') return;
            tCols[key] = { 
                text: key, 
                align: CENTER_ALIGNED_KEYS.has(key) ? 'center' : 'left', 
                visible: true,
                type: REQ_ID_KEYS.has(key) ? 'Modal' : undefined,
                width: REQ_ID_KEYS.has(key) ? '125px' : undefined
            };
            dCols[key] = true;
        });
        return { tableColumns: tCols, defaultTableColumns: dCols };
    }, [reportData]);

    const formDatas = [
        { name: "From Date",      type: "Date" },
        { name: "To Date",        type: "Date" },
        { name: "Status",         type: "Select", options: statuses },
        { name: "Requisition ID", type: "Select", options: requisitions },
        { name: "Recruiter",      type: "Select", options: recruiters },
        { name: "Skills",         type: "Select", options: skills }
    ];

     return (
        <div className="container-fluid" style={{ backgroundColor: "#F2F5FA", padding: "15px", height: "100vh" }}>
            <div className="card border-0 shadow-sm rounded-4" style={{ backgroundColor: "#FFFFFF", overflow: "hidden", height: "100%" }}>
                <div className="card-body p-3">
                    {/* position:relative anchors the overlay to this box, not the wide table */}
                    <div style={{ position: 'relative' }} ref={wrapperRef}>
                        <CRDynamic_PR_table
                            data={reportData}
                            columns={tableColumns}
                            defaultColumns={defaultTableColumns}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            isApiStatus={apiStatus}
                            isFilterRequired={true}
                            formDatas={formDatas}
                            handleChange={handleChange}
                            handleReset={handleReset}
                            handleGetData={fetchReport}
                            filterFormData={filterFormData}
                            handleModalColumn={handleModalColumn}
                        />

                        {/* Overlay: visible only while API call is in progress */}
                        {apiStatus === undefined && (
                            <div style={{
                                position       : 'absolute',
                                top            : headerOffset || 0,
                                left           : 0,
                                right          : 0,
                                bottom         : bottomOffset || 0,
                                display        : 'flex',
                                flexDirection  : 'column',
                                alignItems     : 'center',
                                justifyContent : 'center',
                                backgroundColor: 'rgba(255,255,255,0.75)',
                                zIndex         : 10,
                                pointerEvents  : 'none'
                            }}>
                                <FaSpinner className="spinner" />
                                <span className="mt-2">Loading</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Candidate Leads Details Modal */}
            <Modal show={showCandidateModal} onHide={() => setShowCandidateModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
                    <Modal.Title style={{ fontSize: "17px", fontWeight: "600", color: "#27568c" }}>
                        Requisition Details ({modalReqCode})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: "20px" }}>
                    {modalLoading ? (
                        <div className="text-center py-4">
                            <FaSpinner className="spinner" style={{ fontSize: "24px", color: "#27568c" }} />
                            <div className="mt-2 text-muted" style={{ fontSize: "13px" }}>Loading Candidates...</div>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex align-items-center gap-3 mb-3 p-2 rounded" style={{ backgroundColor: "#f1f5f9" }}>
                                <div className="px-3 py-1.5 rounded-pill" style={{ backgroundColor: "#d1e7dd", color: "#0f5132", fontWeight: "600", fontSize: "13px" }}>
                                    Total TA Leads: {candidateModalData.taCount}
                                </div>
                                <div className="px-3 py-1.5 rounded-pill" style={{ backgroundColor: "#cfe2ff", color: "#084298", fontWeight: "600", fontSize: "13px" }}>
                                    Total RMG Leads: {candidateModalData.rmgCount}
                                </div>
                            </div>

                            <div className="table-responsive" style={{ maxHeight: "350px", overflowY: "auto" }}>
                                <Table hover bordered className="align-middle mb-0" style={{ fontSize: "13px" }}>
                                    <thead style={{ backgroundColor: "#27568c", color: "#ffffff", position: "sticky", top: 0, zIndex: 10 }}>
                                        <tr>
                                            <th style={{ width: "55px", textTransform: "none", backgroundColor: "#27568c", color: "#ffffff", textAlign: "center" }}>Sr. No</th>
                                            <th style={{ textTransform: "none", backgroundColor: "#27568c", color: "#ffffff" }}>Candidate / Employee</th>
                                            <th style={{ textTransform: "none", backgroundColor: "#27568c", color: "#ffffff" }}>Recruiter / RMG POC</th>
                                            <th style={{ textTransform: "none", backgroundColor: "#27568c", color: "#ffffff" }}>Current Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {candidateModalData.candidates?.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="text-center text-muted py-3">No candidates found for this requirement.</td>
                                            </tr>
                                        ) : (
                                            candidateModalData.candidates.map((cand, idx) => {
                                                const isTA = cand.Type === "TA";
                                                const textColor = isTA ? "#155724" : "#004085";
                                                return (
                                                    <tr key={cand.ID || idx} style={{ backgroundColor: isTA ? "#f4fbf7" : "#f4f8fe" }}>
                                                        <td className="text-center" style={{ color: textColor }}>{idx + 1}</td>
                                                        <td className="fw-bold" style={{ color: textColor }}>{cand.CandidateName || "-"}</td>
                                                        <td style={{ color: textColor }}>{cand.RecruiterName || "-"}</td>
                                                        <td style={{ color: textColor }}>{cand.LatestStatus || "-"}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: "1px solid #dee2e6" }}>
                    <Button variant="secondary" onClick={() => setShowCandidateModal(false)} style={{ fontSize: "13px" }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default RequisitionTrackerReport;
